import mdAuth from 'md-authjs';
import create from 'zustand';

import { LOGIN_URL, AUTH_HOST, HOME } from '@/config';
import { REQUEST_FAILED, REQUEST_SUCCESS } from '@/config/appConstants';
import axios from '@/lib/axios';
import { iAuthInfo } from '@/types';
import storage from '@/utils/storage';

const authFilter = {
  init() {
    /* Nothing to do here */
  },
};

export type ConfigType = {
  loginUrl: string;
  authHost: string;
  home: string;
  i18n: any;
};

let authService: any;

const getConfig = (force = ''): ConfigType => {
  let pathname = window.location.pathname;
  pathname = pathname.replace('/', '');

  return {
    loginUrl: LOGIN_URL + (pathname ? `?path=${pathname}` : ''),
    authHost: AUTH_HOST + force,
    home: HOME,
    i18n: {
      auth401title: 'Session expired (Sesión inválida)',
      auth401message:
        'Your session has expired. Please log in again.\n(Tu sesión ha expirado. Por favor, vuelve a introducir tus datos de acceso)',
      auth403title: 'Forbidden (Error de acceso)',
      auth403message:
        'You are not authorized to access this resource.\n(No dispones de permisos para acceder a este módulo o aplicación. Serás redirigido a la sección de mi cuenta de MDirector.)',
      auth500title: 'Internal Server Error (Error interno del servidor)',
      auth500message:
        'Something went wrong on our side. Please try again later.\n(Ha ocurrido un error por nuestra parte. Por favor, inténtalo de nuevo más tarde.)',
    },
  };
};

const getConfigCompanyId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const companyId = urlParams.get('company_id') || null;
  if (!companyId) return storage.getCompanyId();
  return companyId;
};

const login = (callback: any) => {
  const config = getConfig(getConfigCompanyId() ? '?company_id=' + getConfigCompanyId() : '');
  const options = { config, authTicket: getAuthTicket(config.authHost), authFilter, DEBUG: false };

  authService = mdAuth(options);
  authService.login(callback);
};

const getAuthTicket = (authHost: string): any => {
  return {
    get(callbackOk: any, callbackError: any) {
      return axios
        .get(authHost, { withCredentials: true })
        .then((response: any) => {
          if (callbackOk) {
            return callbackOk(response);
          }
        })
        .catch((error: any) => {
          if (!error.response && callbackError) {
            return callbackError(error);
          }

          if (error?.response?.status === 401) {
            if (callbackError) {
              return callbackError(error.response);
            }
          }
          /* not hit since no 401 */
        });
    },
  };
};

const newLogin = (callback: any) => {
  const config = getConfig(
    getConfigCompanyId() ? '?force=true&company_id=' + getConfigCompanyId() : '?force=true'
  );
  const options = { config, authTicket: getAuthTicket(config.authHost), authFilter, DEBUG: false };
  authService = mdAuth(options);
  return authService.newLogin(callback);
};

const isLogged = () => {
  const config = getConfig(getConfigCompanyId() ? '?company_id=' + getConfigCompanyId() : '');
  const options = { config, authTicket: getAuthTicket(config.authHost), authFilter, DEBUG: false };
  authService = mdAuth(options);
  const authInfo = window.localStorage.getItem(authService.getAuthInfo().localStorageKey);

  return authInfo && JSON.parse(authInfo) ? true : false;
};

const removeItemInLocalStorage = () => {
  const config = getConfig(getConfigCompanyId() ? '?company_id=' + getConfigCompanyId() : '');
  const options = { config, authTicket: getAuthTicket(config.authHost), authFilter, DEBUG: false };
  authService = mdAuth(options);

  window.localStorage.removeItem(authService.getAuthInfo().localStorageKey);
};

type Auth = {
  login: (callback: any) => any;
  newLogin: (callback: any) => any;
  getAuthInfo: () => iAuthInfo;
  isLogged: () => boolean;
  removeItemInLocalStorage: () => void;
};

export const useAuth = create<Auth>(() => ({
  login: (callback: any) => {
    login((isLogged: boolean) => {
      isLogged && callback && callback(REQUEST_SUCCESS);
      !isLogged && callback && callback(REQUEST_FAILED);
    });
  },

  newLogin: async (callback: any) => {
    await newLogin((isLogged: boolean) => {
      isLogged && callback && callback();
      return true;
    });
    return true;
  },

  getAuthInfo: () => (authService ? authService.getAuthInfo() : {}),

  isLogged: () => isLogged(),

  removeItemInLocalStorage: () => removeItemInLocalStorage(),
}));
